import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { launchModalsSx } from 'modules/campaign-creation/styles';

type Props = {
  isOpen: boolean;
  onCountdownComplete: () => void;
  onAbort: () => void;
};

const COUNTDOWN_SECONDS = 5;

export const LaunchCountdownModal: React.FC<Props> = ({
  isOpen,
  onCountdownComplete,
  onAbort
}) => {
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const isCompletingRef = useRef(false);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    // Reset state when modal opens (not when it closes, to avoid flash during close animation)
    if (isOpen && !wasOpenRef.current) {
      setCountdown(COUNTDOWN_SECONDS);
      isCompletingRef.current = false;
    }
    wasOpenRef.current = isOpen;

    if (!isOpen) {
      return;
    }

    // Handle countdown completion
    if (countdown === 0 && !isCompletingRef.current) {
      isCompletingRef.current = true;
      onCountdownComplete();
      return;
    }

    // Tick down
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isOpen, countdown, onCountdownComplete]);

  // Display countdown, but show 1 as minimum (not 0)
  const displayCountdown = Math.max(countdown, 1);

  // Only 'backdropClick' is possible since disableEscapeKeyDown is set on Dialog
  const handleClose = (_event: object, reason: 'backdropClick') => {
    if (reason === 'backdropClick') {
      return;
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      disableEscapeKeyDown // Prevent accidental close during countdown
    >
      <DialogContent sx={launchModalsSx.modalContent}>
        <Typography sx={launchModalsSx.countdownTitle}>
          Launching in {displayCountdown}
        </Typography>
        <Typography sx={launchModalsSx.modalSubtitle}>
          Your campaign is about to go live. If you don't want to launch now,
          abort the launch.
        </Typography>
        <Button
          variant="primary"
          sx={launchModalsSx.abortButton}
          onClick={onAbort}
        >
          Abort launch
        </Button>
      </DialogContent>
    </Dialog>
  );
};
